import {
  SET_STATISTICS_LOADING,
  SET_STATISTICS_LOADING_EXPORT,
  SET_REGISTRATIONS_DATA,
  SET_CAPACITY_DATA,
  SET_TASK_CAPACITY_DATA,
  SET_SUBMIT_TASK_COUNT_DATA,
  SET_FUNDS_DETAILS_DATA,
  SET_COUPON_DETAILS_DATA,
  SET_REPURCHASE_RATE_DATA,
  SET_USER_BALANCE_DATA,
  SET_RECHARGE_DETAILS_DATA,
  SET_INCOME_DATA,
  SET_REMARK_DATA,
} from './mutation-type'
import api from '@/api'
import { objectTrim, getHeaderName } from '@/utils'
import files from 'file-saver'
import achievement from './achievement'
import { checkUserName } from '@/views/User/utils'

const ExportAPIMap = Object.freeze({
  capacity: api.statisticsApi.exportStatisticsCapacity,
  taskCapacity: api.statisticsApi.exportStatisticsTaskCapacity,
  registrations: api.statisticsApi.exportStatisticsRegistrations,
  submitTaskCount: api.statisticsApi.exportStatisticsSubmitTaskCount,
  fundsDetails: api.statisticsApi.exportStatisticsFundsDetails,
  couponDetails: api.statisticsApi.exportStatisticsCouponDetails,
  repurchaseRate: api.statisticsApi.exportStatisticsRepurchaseRate,
  userBalance: api.statisticsApi.exportStatisticsUserBalance,
  rechargeDetails: api.statisticsApi.exportRecharge,
  income: api.statisticsApi.exportIncomeVerification,
  incomeCalculation: api.statisticsApi.exportIncomeCalculation,
  remark: api.statisticsApi.addRemark,
  cardFrameDetails: api.statisticsApi.exportAbnormalJobs,
  rentalNodeDetails: api.statisticsApi.exportRentalNodeDetails,
  transferRecord: api.statisticsApi.exportTransferRecord,
  userOfProject: api.statisticsApi.exportUserOfProject,
  questionnaireList: api.statisticsApi.exportQuestionnaire,
  countAbnormalJobs: api.statisticsApi.exportCountAbnormalJobs,
  exportRevenueCumulative: api.statisticsApi.exportRevenueCumulative,
  exportMiGuYunIncomeCalculation: api.statisticsApi.exportMiGuYunIncomeCalculation,
  userFeedbackNew: api.statisticsApi.exportUserFeedbackNew,
  exportClientFeedback: api.statisticsApi.exportClientFeedback,
})
const DataInfoMap = Object.freeze({
  capacity: { api: api.statisticsApi.getStatisticsCapacity, commit: SET_CAPACITY_DATA },
  taskCapacity: { api: api.statisticsApi.getStatisticsTaskCapacity, commit: SET_TASK_CAPACITY_DATA },
  registrations: {
    api: api.statisticsApi.getStatisticsRegistrations,
    commit: SET_REGISTRATIONS_DATA,
  },
  submitTaskCount: {
    api: api.statisticsApi.getStatisticsSubmitTaskCount,
    commit: SET_SUBMIT_TASK_COUNT_DATA,
  },
  fundsDetails: {
    api: api.statisticsApi.getStatisticsFundsDetails,
    commit: SET_FUNDS_DETAILS_DATA,
  },
  couponDetails: {
    api: api.statisticsApi.getStatisticsCouponDetails,
    commit: SET_COUPON_DETAILS_DATA,
  },
  repurchaseRate: {
    api: api.statisticsApi.getStatisticsRepurchaseRate,
    commit: SET_REPURCHASE_RATE_DATA,
  },
  userBalance: { api: api.statisticsApi.getStatisticsUserBalance, commit: SET_USER_BALANCE_DATA },
  rechargeDetails: {
    api: api.statisticsApi.getStatisticsRecharge,
    commit: SET_RECHARGE_DETAILS_DATA,
  },
  income: {
    api: api.statisticsApi.getIncomeVerification,
    commit: SET_INCOME_DATA,
  },
  remark: {
    api: api.statisticsApi.addRemark,
    commit: SET_REMARK_DATA,
  },
})

const state = {
  loadingDatas: false,
  loadingExport: false,
  /**
   * @typedef couponDetailsDataItems
   * @property {Long} items.mainUserId 主账号ID
   * @property {Long} items.userId 当前账号ID
   * @property {String} items.company 公司名称
   * @property {Byte} items.currency 币种 1/人民币 2/美元
   * @property {Date} items.effectiveDate 优惠券生效时间.
   * @property {Date} items.expireDate 优惠券过期时间.
   * @property {Integer} items.term 有效期（天）.
   * @property {Byte} items.getWay 渲染券获取方式.
   * @property {Number} items.couponAmount 优惠券总额.
   * @property {Byte} items.type 券类型 1 GPU券 2 CPU券  3 通用券  4 CPU青云券.
   * @property {String} items.saleMan 业务员
   * @property {Number} items.couponBalance 余额
   */
  couponDetailsData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {couponDetailsDataItems[]} */
    items: [],
  },
  /**
   * @typedef fundsDetailsDataItems
   * @property {Long} items.mainUserId 主账号ID
   * @property {Long} items.userId 当前账号ID
   * @property {String} items.company 公司名称
   * @property {Integer} items.payMethod 充值方式
   * @property {Date} items.confirmDate 确认日期
   * @property {Number} items.amount 充值金额
   * @property {String} items.transactionId 交易号ID(支付宝,微信，Paypal的真实交易流水号)
   * @property {Byte} items.currency 币种
   * @property {Byte} items.adminName 管理员姓名
   * @property {String} items.saleMan 业务员
   */
  fundsDetailsData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {fundsDetailsDataItems[]} */
    items: [],
  },

  /**
   * @typedef registrationsDataItems
   * @property {Date} items.registerDay 注册日期
   * @property {String} items.registerMounth 注册月份
   * @property {Number} items.zone 国内外区域(1国内 2国外)
   * @property {Number} items.areaId 区域id
   * @property {Number} items.userType 用户类型  1/普通用户，2/代理商.
   * @property {Number} items.userCount 用户量.
   * @property {Number} items.effectiveUserCount 有效用户量.
   * @property {Byte} items.businessType 业务类型(0 效果图 1 动画)
   * @property {Integer} items.zombieCount 僵尸用户量
   */
  registrationsData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {registrationsDataItems[]} */
    items: [],
  },

  /**
   * @typedef repurchaseRateDataItems
   * @property {String} items.currentPeriod 现期间
   * @property {String} items.lastPeriod 同比期间
   * @property {Number} items.repurchaseRate 复购率
   */
  /** @type {repurchaseRateDataItems[]} */
  repurchaseRateData: [],

  /**
   * @typedef taskCountDataItems
   * @property {String} items.submitDay 提交日期
   * @property {Number} items.zone 国内外区域(1国内 2国外)
   * @property {Number} items.areaId 区域id
   * @property {Number} items.taskCount 提交任务数量
   * @property {Number} items.platform 平台.
   */
  taskCountData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {taskCountDataItems[]} */
    items: [],
  },
  /**
   * @typedef balanceDataItems
   * @property {Long} items.mainUserId 主账号ID
   * @property {Long} items.userId 当前账号ID
   * @property {String} items.company 公司名称
   * @property {String} items.saleMan 业务员
   * @property {Byte} items.currency 币种 1/人民币 2/美元
   * @property {Number} items.balance 余额.
   * @property {Number} items.gpuCouponBalance GPU券余额.
   * @property {Number} items.cpuCouponBalance CPU券余额.
   * @property {Number} items.generalCouponBalance 通用券余额.
   * @property {Number} items.cpuQyCouponBalance CPU青云券余额.
   * @property {Number} items.gpuQyCouponBalance GPU青云券余额.
   * @property {Date}  items.statisticsTime 统计时间
   */
  balanceData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {balanceDataItems[]} */
    items: [],
  },
  /**
   * @typedef capacityDataItems
   * @property {Number} items.mainUserId 主账号ID
   * @property {Number} items.taskUserId 关联任务的用户ID
   * @property {String} items.company 公司名称
   * @property {Number} items.feeUserId 扣费的用户ID
   * @property {Number} items.renderDuration 渲染时长(秒)
   * @property {Number} items.renderHour 渲染时长(小时)
   * @property {Number} items.feeAmount 余额扣费
   * @property {Number} items.couponFeeAmount 渲染券扣费
   * @property {Number} items.currency 币种
   * @property {Number} items.platform 平台
   * @property {Number} items.returnType 返还类型 0/未返还 1/出错返还 2/抵扣返还
   * @property {Number} items.price 机时单价
   * @property {String} items.saleMan 业务员
   */
  capacityData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {capacityDataItems[]} */
    items: [],
  },
  /**
   * @typedef taskCapacityDataItems
   * @property {Number} items.taskId 任务号
   * @property {Number} items.feeUserId 扣费的用户ID
   * @property {String} items.company 公司名称
   * @property {Number} items.renderDuration 渲染时长(秒)
   * @property {Number} items.renderHour 渲染时长(小时)
   * @property {Number} items.feeAmount 余额扣费
   * @property {Number} items.couponFeeAmount 渲染券扣费
   * @property {Number} items.currency 币种
   * @property {Number} items.platform 平台
   * @property {Number} items.returnType 返还类型 0/未返还 1/出错返还 2/抵扣返还
   * @property {Number} items.price 机时单价
   * @property {String} items.saleMan 业务员
   */
  taskCapacityData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {taskCapacityDataItems[]} */
    items: [],
  },
  // 充值明细
  rechargeDetailsData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    items: [],
    rmbTotalAmount: 0,
    usdTotalAmount: 0,
  },
  /**
    * @typedef incomeDataItems
    * @property {Number} items.id
    * @property {Number} items.userId
    * @property {String} items.userName
    * @property {Number} items.openingBalance 期初余额.
    * @property {Number} items.recharge
    * @property {Number} items.consume
    * @property {Number} items.closingBalance 期终余额.
    * @property {Number} items.currency 币种 1/rmb 2/usd.
    * @property {String} items.statisticsDay 统计日期.
    * @property {Number} items.balanceDifference 余额差异.
    * @property {String} items.remark
   */
  incomeData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    /** @type {incomeDataItems[]} */
    items: [],
  },
  remarkData: {
    pageNum: 1,
    pageCount: 0,
    total: 0,
    size: 50,
    items: [],
    remark: '',
    ids: [],
  },
}

const mutations = {
  [SET_STATISTICS_LOADING](state, payload) {
    state.loadingDatas = payload
  },
  [SET_STATISTICS_LOADING_EXPORT](state, payload) {
    state.loadingExport = payload
  },
  [SET_COUPON_DETAILS_DATA](state, payload) {
    state.couponDetailsData = payload
  },
  [SET_FUNDS_DETAILS_DATA](state, payload) {
    state.fundsDetailsData = payload
  },
  [SET_REGISTRATIONS_DATA](state, payload) {
    state.registrationsData = payload
  },
  [SET_USER_BALANCE_DATA](state, payload) {
    state.balanceData = payload
  },
  [SET_SUBMIT_TASK_COUNT_DATA](state, payload) {
    state.taskCountData = payload
  },
  [SET_REPURCHASE_RATE_DATA](state, payload) {
    state.repurchaseRateData = payload
  },
  [SET_CAPACITY_DATA](state, payload) {
    state.capacityData = payload
  },
  [SET_TASK_CAPACITY_DATA](state, payload) {
    state.taskCapacityData = payload
  },
  [SET_RECHARGE_DETAILS_DATA](state, payload) {
    state.rechargeDetailsData = payload
  },
  [SET_INCOME_DATA](state, payload) {
    state.incomeData = payload
  },
  [SET_REMARK_DATA](state, payload) {
    state.remarkData = payload
  },
}

const actions = {
  async getDatas({ commit, dispatch }, { query, type }) {
    const dataInfo = DataInfoMap[type]
    const dataQuery = objectTrim(query, false, true)
    commit(SET_STATISTICS_LOADING, true)
    const res = await dataInfo.api(dataQuery)
    const { data, code } = res
    checkUserName({
      code,
      data,
      callback: ({userId}) => {
        const { userName, ...other } = query
        dispatch('getDatas', {
          query: {
            ...other,
            userId,
          },
          type,
        })
      },
    })
    if (res.code === 200) {
      commit(dataInfo.commit, res.data)
    }
    commit(SET_STATISTICS_LOADING, false)
  },
  async exportExcel({ commit, dispatch }, { query, type }) {
    const exportAPI = ExportAPIMap[type]
    const exportQuery = objectTrim(query, false, true)
    commit(SET_STATISTICS_LOADING_EXPORT, true)
    if (query.userId) {
      delete exportQuery.userName
    }
    const res = await exportAPI(exportQuery)
    const { data, headers } = res
    try {
      const fileName = getHeaderName(headers)
      const blob = new Blob([data], { type: 'application/zip' })
      files.saveAs(blob, fileName)
    } catch (error) {
      const reader = new FileReader()
      reader.onload = () => {
        const result = JSON.parse(reader.result)
        const { code, data } = result
        checkUserName({
          code,
          data,
          callback: ({userId}) => {
            const { userName, ...other } = exportQuery
            dispatch('exportExcel', {
              query: {
                ...other,
                userId,
              },
              type,
            })
          },
        })
      }
      reader.readAsText(res.data)
    }
    commit(SET_STATISTICS_LOADING_EXPORT, false)
  },
  async exportToList({ commit }, { query, type }) {
    const exportAPI = ExportAPIMap[type]
    const exportQuery = objectTrim(query, false, true)
    commit(SET_STATISTICS_LOADING_EXPORT, true)
    const res = await exportAPI(exportQuery)
    commit(SET_STATISTICS_LOADING_EXPORT, false)
    return res
  },
  // 添加备注
  async addRemark ({commit}, payload) {
    const {status} = await api.statisticsApi.addRemark(payload)
    commit(SET_REMARK_DATA, true)
    return {status}
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  modules: {
    achievement,
  },
}
